// Belge sayfası + siyah alan çizme / taşıma / boyutlandırma katmanı (fare ve dokunmatik)
import { useEffect, useRef, useState } from 'react';

const MIN = 6;
const CORNERS = ['nw', 'ne', 'sw', 'se'];
const newId = () => Math.random().toString(36).slice(2, 10);
const clamp = (v, a, b) => Math.max(a, Math.min(b, v));

const norm = (r, W, H) => {
  const x = clamp(Math.min(r.x, r.x + r.w), 0, W);
  const y = clamp(Math.min(r.y, r.y + r.h), 0, H);
  return { ...r, x, y, w: clamp(Math.abs(r.w), 0, W - x), h: clamp(Math.abs(r.h), 0, H - y) };
};

const resize = (r, corner, dx, dy) => {
  const n = { ...r };
  if (corner.includes('w')) { n.x = r.x + dx; n.w = r.w - dx; }
  if (corner.includes('e')) n.w = r.w + dx;
  if (corner.includes('n')) { n.y = r.y + dy; n.h = r.h - dy; }
  if (corner.includes('s')) n.h = r.h + dy;
  return n;
};

export const MaskCanvas = ({ page, rects, zoom, drawMode, selectedId, onSelect, onCommit }) => {
  const canvasRef = useRef(null);
  const svgRef = useRef(null);
  const drag = useRef(null);
  const [live, setLive] = useState(null);
  const W = page.width;
  const H = page.height;

  useEffect(() => {
    const c = canvasRef.current;
    c.width = W; c.height = H;
    c.getContext('2d').drawImage(page, 0, 0);
  }, [page, W, H]);

  const pt = (e) => {
    const b = svgRef.current.getBoundingClientRect();
    return { x: clamp(((e.clientX - b.left) / b.width) * W, 0, W), y: clamp(((e.clientY - b.top) / b.height) * H, 0, H) };
  };

  const down = (e) => {
    const p = pt(e);
    const { id, corner } = e.target.dataset;
    if (corner) drag.current = { type: 'resize', id, corner, start: p, orig: rects.find(r => r.id === id) };
    else if (id) drag.current = { type: 'move', id, start: p, orig: rects.find(r => r.id === id) };
    else if (drawMode) drag.current = { type: 'draw', id: newId(), start: p };
    else { onSelect(null); return; }
    onSelect(drag.current.id);
    svgRef.current.setPointerCapture(e.pointerId);
    e.preventDefault();
  };

  const move = (e) => {
    const d = drag.current;
    if (!d) return;
    const p = pt(e);
    const dx = p.x - d.start.x;
    const dy = p.y - d.start.y;
    let r;
    if (d.type === 'draw') r = { id: d.id, x: d.start.x, y: d.start.y, w: dx, h: dy };
    else if (d.type === 'move') r = { ...d.orig, x: clamp(d.orig.x + dx, 0, W - d.orig.w), y: clamp(d.orig.y + dy, 0, H - d.orig.h) };
    else r = resize(d.orig, d.corner, dx, dy);
    setLive(norm(r, W, H));
  };

  const up = () => {
    const d = drag.current;
    drag.current = null;
    if (!d || !live) { setLive(null); return; }
    const ok = live.w >= MIN && live.h >= MIN;
    const next = d.type === 'draw' ? (ok ? [...rects, live] : rects) : rects.map(r => (r.id === d.id ? (ok ? live : r) : r));
    setLive(null);
    if (next !== rects) onCommit(next);
    if (d.type === 'draw' && !ok) onSelect(null);
  };

  const shown = live ? [...rects.filter(r => r.id !== live.id), live] : rects;
  const hs = 12 / zoom;

  return (
    <div className="relative shadow-xl bg-white" style={{ width: W * zoom, height: H * zoom }} data-testid="mask-page">
      <canvas ref={canvasRef} className="absolute inset-0 h-full w-full" />
      <svg ref={svgRef} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none"
        className={`absolute inset-0 h-full w-full touch-none ${drawMode ? 'cursor-crosshair' : 'cursor-default'}`}
        onPointerDown={down} onPointerMove={move} onPointerUp={up} onPointerCancel={up} data-testid="mask-overlay">
        {shown.map(r => (
          <g key={r.id}>
            <rect x={r.x} y={r.y} width={r.w} height={r.h} fill="#000" data-id={r.id} className="cursor-move"
              stroke={r.id === selectedId ? '#ef4444' : 'none'} strokeWidth={2 / zoom} strokeDasharray={`${6 / zoom} ${4 / zoom}`} data-testid="mask-rect" />
            {r.id === selectedId && CORNERS.map(c => (
              <rect key={c} data-id={r.id} data-corner={c} width={hs} height={hs} fill="#fff" stroke="#ef4444" strokeWidth={2 / zoom}
                x={(c.includes('w') ? r.x : r.x + r.w) - hs / 2} y={(c.includes('n') ? r.y : r.y + r.h) - hs / 2}
                className={c === 'nw' || c === 'se' ? 'cursor-nwse-resize' : 'cursor-nesw-resize'} data-testid={`mask-handle-${c}`} />
            ))}
          </g>
        ))}
      </svg>
    </div>
  );
};
